import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Phone, MessageSquare, Facebook, Instagram, RefreshCw,
  AlertTriangle, Info, TrendingUp, TrendingDown, X,
} from 'lucide-react';
import { fetchContactBoard } from '../services/api';

// ── Contact Board ─────────────────────────────────────────────────────────────
// What came in, by channel, for the week. Built from the Contact Center Brain task
// of the same name, to the order Rachael gave on 25 Aug 2026:
//
//   Email, Phone, Contact us form, Facebook, Instagram. Voicemail is off the board.
//
// The order is HERS and the board never re-sorts by volume. It is the order her team
// works in, and a quiet week on email must not silently promote phone to the top and
// change what the board means. The order lives once, server side, in contact-board.js.
//
// Every row says where its number came from and what it cannot see, because the five
// numbers do not have the same standing: two are read daily, one is live, one depends
// on an hourly sync, and one is a channel Meta has not granted us yet. A board that
// showed all five as bare figures would be five different kinds of confidence
// wearing the same clothes.

const ICONS = {
  email: Mail,
  phone: Phone,
  contact_form: MessageSquare,
  facebook: Facebook,
  instagram: Instagram,
};

const STATUS_NOTE = {
  ok: null,
  partial: { label: 'Partial', color: 'var(--orange)', bg: '#fff1e8' },
  unavailable: { label: 'Unavailable', color: 'var(--red)', bg: '#fde8e8' },
  not_connected: { label: 'Not connected', color: '#5b6470', bg: '#eceff1' },
};

// Windows are worked out in Eastern time, because the team's week is. The server is
// handed real instants and never has to guess a timezone.
//
// The offset is measured at the Monday being computed, not at "now", so the week does
// not slide by an hour on the two Sundays a year when the clocks change mid-week.
const etOffsetMs = (at) =>
  new Date(at.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()
  - new Date(at.toLocaleString('en-US', { timeZone: 'America/New_York' })).getTime();

function startOfEtWeek() {
  const now = new Date();
  const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const monday = new Date(etNow);
  monday.setDate(etNow.getDate() - ((etNow.getDay() + 6) % 7)); // Sunday belongs to the week just ending
  monday.setHours(0, 0, 0, 0);
  const provisional = new Date(monday.getTime() + etOffsetMs(now));
  return new Date(monday.getTime() + etOffsetMs(provisional));
}

const PRESETS = [
  { key: 'week', label: 'This week', range: () => ({ start: startOfEtWeek(), end: new Date() }) },
  { key: '7', label: 'Last 7 days', range: () => ({ start: new Date(Date.now() - 7 * 86400000), end: new Date() }) },
  { key: '30', label: 'Last 30 days', range: () => ({ start: new Date(Date.now() - 30 * 86400000), end: new Date() }) },
];

const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const fmtDay = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function Change({ now, prior }) {
  if (typeof now !== 'number' || typeof prior !== 'number' || prior === 0) return null;
  const pct = Math.round(((now - prior) / prior) * 100);
  if (pct === 0) return <span className="kpi-compare">level with the period before</span>;
  const up = pct > 0;
  const Arrow = up ? TrendingUp : TrendingDown;
  return (
    <span className="kpi-compare" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Arrow size={12} />
      {Math.abs(pct)}% vs the period before ({prior})
    </span>
  );
}

// ── One category, opened ──────────────────────────────────────────────────────
// The screen that was missing. Rachael was asked to correct the category list and
// answered "I am not sure what emails fall under other, I don't see any that do",
// which was true: the list lived in code and nothing ever showed her a message in it.
// Re-filing one here is final, and the scanner will not undo it.
function CategoryModal({ category, window: win, onClose, onCorrected }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState('');

  const load = useCallback(() => {
    fetchContactBoard({ action: 'messages', category, start: win.start, end: win.end })
      .then(setData)
      .catch((e) => setErr(e.message));
  }, [category, win.start, win.end]);

  useEffect(() => { load(); }, [load]);

  const refile = async (messageId, to) => {
    setSaving(messageId);
    try {
      await fetchContactBoard({ action: 'correct', messageId, category: to });
      load();
      onCorrected && onCorrected();
    } catch (e) { setErr(e.message); } finally { setSaving(''); }
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,20,40,.5)', zIndex: 90, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 24, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, width: 'min(920px,100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: 'var(--navy)', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9dc4dd' }}>Filed under</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{(data && data.label) || category}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto' }}>
          {err ? <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>{err}</div> : null}
          {!data ? <div style={{ color: 'var(--gray-500)' }}>Opening…</div> : null}
          {data && data.messages.length === 0 ? (
            <div style={{ color: 'var(--gray-500)' }}>Nothing in this category for the dates on screen.</div>
          ) : null}

          {data ? data.messages.map((m) => (
            <div key={m.message_id} style={{ borderTop: '1px solid var(--gray-200)', padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--navy)', fontSize: 14 }}>{m.customer_name || m.customer_email}</strong>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                  {fmtWhen(m.received_at)}
                  {m.order_number ? ` · order ${m.order_number}` : ''}
                  {m.classified_by === 'human' ? ' · filed by hand' : ''}
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--navy)', marginTop: 2 }}>{m.subject || '(no subject)'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>{m.excerpt}</div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Should be</span>
                <select className="form-select" style={{ fontSize: 12.5, padding: '3px 8px', width: 'auto' }}
                  disabled={saving === m.message_id}
                  value={category}
                  onChange={(e) => refile(m.message_id, e.target.value)}>
                  {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {saving === m.message_id ? <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>saving…</span> : null}
              </div>
            </div>
          )) : null}
        </div>

        <div style={{ borderTop: '1px solid var(--gray-200)', padding: '10px 20px', fontSize: 12.5, color: 'var(--gray-500)' }}>
          Changing one here is final. The daily scan will not put it back, and every change is kept as a record of
          where the sorting gets things wrong, which is what the next fix to the categories is built from.
        </div>
      </div>
    </div>
  );
}

export default function ContactBoard() {
  const [preset, setPreset] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openCat, setOpenCat] = useState(null);

  const load = useCallback(() => {
    const p = PRESETS.find((x) => x.key === preset) || PRESETS[0];
    const { start, end } = p.range();
    setLoading(true);
    setErr('');
    fetchContactBoard({ start: start.toISOString(), end: end.toISOString() })
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [preset]);

  useEffect(() => { load(); }, [load]);

  const channels = (data && data.channels) || [];
  const max = channels.reduce((a, c) => Math.max(a, typeof c.count === 'number' ? c.count : 0), 0);
  const sf = data && data.serviceFailure;
  const sfShare = sf && sf.of ? Math.round((sf.count / sf.of) * 100) : null;

  return (
    <div>
      <div className="breadcrumb">
        <span>Communications</span>
        <span> / </span>
        <span>Contact Board</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Contact Board</h1>
          <div style={{ color: 'var(--gray-500)', fontSize: 14, marginTop: 4 }}>
            What customers used to reach us, in the order the team works them.
            {data && data.window ? ` ${fmtDay(data.window.start)} to ${fmtDay(data.window.end)}.` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={preset === p.key ? 'btn btn-primary' : 'btn btn-outline'}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button className="btn btn-ghost" onClick={load} title="Reload">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {err ? (
        <div className="card" style={{ borderLeft: '4px solid var(--red)' }}>
          <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={18} color="var(--red)" />
            <div>
              <strong>The board could not load.</strong>
              <div style={{ color: 'var(--gray-500)', fontSize: 13, marginTop: 4 }}>{err}</div>
            </div>
          </div>
        </div>
      ) : null}

      {loading && !data ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--gray-500)' }}>Counting…</div></div>
      ) : null}

      {data ? (
        <>
          <div className="kpi-grid" style={{ marginBottom: 18 }}>
            <div className="kpi-card">
              <div className="kpi-label">Contacts counted</div>
              <div className="kpi-value">{data.total}</div>
              <Change now={data.total} prior={data.priorTotal} />
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Something went wrong</div>
              <div className="kpi-value">{sf ? sf.count : 0}{sfShare != null ? <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-500)' }}> · {sfShare}%</span> : null}</div>
              <span className="kpi-compare">Late, damaged, quality or short, of the {sf ? sf.of : 0} we classify</span>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Crab and Cruise</div>
              <div className="kpi-value">{data.crabAndCruise.count}</div>
              <span className="kpi-compare">Counted on its own line, not in the figures above</span>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-header">
              <span>By channel</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Rachael&rsquo;s order · voicemail off the board
              </span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {channels.map((c, i) => {
                const Icon = ICONS[c.key] || Mail;
                const badge = STATUS_NOTE[c.status];
                const known = typeof c.count === 'number';
                const width = known && max > 0 ? Math.max(2, Math.round((c.count / max) * 100)) : 0;
                return (
                  <div
                    key={c.key}
                    style={{
                      display: 'flex', gap: 14, padding: '16px 20px', alignItems: 'flex-start',
                      borderTop: i === 0 ? 'none' : '1px solid var(--gray-200)',
                      opacity: known ? 1 : 0.75,
                    }}
                  >
                    <div style={{
                      width: 26, height: 26, borderRadius: 6, background: 'var(--peach)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2,
                    }}>
                      <Icon size={15} color="var(--navy)" />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gray-500)' }}>{i + 1}</span>
                        <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>{c.label}</span>
                        {badge ? (
                          <span style={{
                            fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px',
                            color: badge.color, background: badge.bg, borderRadius: 3, padding: '2px 6px',
                          }}>{badge.label}</span>
                        ) : null}
                        {c.serviceFailures ? (
                          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {c.serviceFailures} of them something went wrong
                          </span>
                        ) : null}
                        {typeof c.abandoned === 'number' && c.abandoned > 0 ? (
                          <span style={{ fontSize: 12, color: 'var(--red)' }}>{c.abandoned} rang off before we answered</span>
                        ) : null}
                      </div>

                      <div style={{ height: 6, background: '#eef1f4', borderRadius: 3, margin: '10px 0 8px', overflow: 'hidden' }}>
                        <div style={{ width: `${width}%`, height: '100%', background: 'var(--orange)' }} />
                      </div>

                      <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
                        {c.basis}
                        {c.caveat ? <span style={{ color: 'var(--orange)' }}> {c.caveat}</span> : null}
                        {c.syncedAt ? <span> Last checked {fmtWhen(c.syncedAt)}.</span> : null}
                        {c.key === 'facebook' && !c.syncedAt ? (
                          <span style={{ color: 'var(--orange)' }}> The hourly check has not recorded a run yet, so this may be behind.</span>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 92 }}>
                      <div style={{ fontSize: 26, fontWeight: 700, color: known ? 'var(--navy)' : 'var(--gray-500)' }}>
                        {known ? c.count : '—'}
                      </div>
                      <div style={{ fontSize: 11 }}><Change now={c.count} prior={c.prior} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-header">
              <span>What they were about</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Email and contact form only
              </span>
            </div>
            <div className="card-body">
              {data.categories.length === 0 ? (
                <div style={{ color: 'var(--gray-500)' }}>Nothing classified in this window.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {data.categories.map((c) => (
                    <button key={c.key} onClick={() => setOpenCat(c.key)} title="See these messages"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                        border: `1px solid ${c.serviceFailure ? '#f3c9c9' : 'var(--gray-200)'}`,
                        background: c.serviceFailure ? '#fde8e8' : '#fff',
                        borderRadius: 20, padding: '5px 12px', fontSize: 13, font: 'inherit',
                      }}>
                      <span style={{ color: c.serviceFailure ? 'var(--red)' : 'var(--navy)' }}>{c.label}</span>
                      <strong style={{ color: 'var(--navy)' }}>{c.n}</strong>
                    </button>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--gray-500)' }}>
                Click a category to read the messages in it. If one is filed wrong, change it there and it stays changed.
                Phone and Facebook are not classified, so they are counted above but not broken down here.
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span>Where these numbers come from</span></div>
            <div className="card-body" style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.65 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Info size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div>
                    Email and the contact form are read daily from the mailboxes customers actually reach,
                    {data.freshness.mailSince ? ` collected since ${fmtDay(data.freshness.mailSince)}` : ''}
                    {data.freshness.mailScan ? `, last read ${fmtWhen(data.freshness.mailScan.ran_at)}` : ''}.
                    A window that starts earlier than that is marked partial rather than shown as a quiet week.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Phone is counted live from the contact centre and includes calls that rang off before anyone
                    answered. Voicemail is excluded everywhere, per Rachael, 25 August.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Facebook comes from the Unified Inbox, refreshed hourly.
                    {data.freshness.socialSync ? ` Last checked ${fmtWhen(data.freshness.socialSync.ran_at)}.` : ''}
                    Instagram messages cannot be counted until Meta grants the Instagram messaging permission on our app.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    One conversation counts once, however many times the customer wrote. Crab and Cruise shares the same
                    mailboxes and is kept on its own line so an event week cannot inflate the seafood numbers.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {openCat && data ? (
        <CategoryModal
          category={openCat}
          window={data.window}
          onClose={() => setOpenCat(null)}
          onCorrected={load}
        />
      ) : null}
    </div>
  );
}
